export interface VideoSource {
  type: 'youtube' | 'google-drive' | 'direct' | 'vimeo' | 'hls' | 'zoom' | 'zoom-recording' | 'unknown';
  url: string;
  embedUrl?: string;
  thumbnailUrl?: string;
  isRecording?: boolean;
  recordingId?: string;
}

/**
 * Extract YouTube video ID from various YouTube URL formats
 */
export const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
    /youtube\.com\/watch\?.*&v=([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
};

/**
 * Extract Google Drive file ID from Google Drive sharing URL
 */
export const extractGoogleDriveId = (url: string): string | null => {
  const patterns = [
    /drive\.google\.com\/file\/d\/([^\/]+)/,
    /drive\.google\.com\/open\?id=([^&\n?#]+)/,
    /drive\.google\.com\/uc\?id=([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
};

/**
 * Extract Zoom meeting ID from Zoom URL
 */
export const extractZoomMeetingId = (url: string): string | null => {
  const patterns = [
    /zoom\.us\/j\/(\d+)/,
    /zoom\.us\/my\/([^\/\?]+)/,
    /zoom\.us\/meeting\/register\/[^\/]+\/([^\/\?]+)/,
    /zoom\.us\/webinar\/register\/[^\/]+\/([^\/\?]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
};

/**
 * Extract Zoom recording ID from cloud recording URL
 */
export const extractZoomRecordingId = (url: string): string | null => {
  const patterns = [
    /zoom\.us\/rec\/share\/([^\/\?]+)/,
    /zoom\.us\/rec\/play\/([^\/\?]+)/,
    /zoom\.us\/rec\/download\/([^\/\?]+)/,
    /zoom\.us\/rec\/[^\/]+\/([^\/\?]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
};

/**
 * Check if Zoom URL is a cloud recording
 */
export const isZoomRecording = (url: string): boolean => {
  const lowerUrl = url.toLowerCase();
  return lowerUrl.includes('zoom.us/rec/');
};

/**
 * Extract Zoom meeting password from URL parameters
 */
export const extractZoomPassword = (url: string): string | null => {
  // First try different password parameter formats in the URL
  const urlPatterns = [
    /[?&]pwd=([^&\n?#]+)/,
    /[?&]password=([^&\n?#]+)/,
    /[?&]passcode=([^&\n?#]+)/,
    /[?&]pass=([^&\n?#]+)/
  ];
  
  for (const pattern of urlPatterns) {
    const match = url.match(pattern);
    if (match) {
      return decodeURIComponent(match[1]);
    }
  }
  
  // If no passcode in URL, try to extract from text that might contain both URL and passcode
  // Look for patterns like "Passcode: xyz" or "Password: xyz" after the URL
  const textPatterns = [
    /Passcode:\s*([^\s\n]+)/i,
    /Password:\s*([^\s\n]+)/i,
    /Pwd:\s*([^\s\n]+)/i,
    /Pass:\s*([^\s\n]+)/i
  ];
  
  for (const pattern of textPatterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1].trim();
    }
  }
  
  return null;
};

/**
 * Extract additional Zoom URL parameters
 */
export const extractZoomUrlParams = (url: string) => {
  const params: { [key: string]: string } = {};
  
  // Extract common Zoom URL parameters
  const paramPatterns = [
    { key: 'pwd', pattern: /[?&]pwd=([^&\n?#]+)/ },
    { key: 'password', pattern: /[?&]password=([^&\n?#]+)/ },
    { key: 'passcode', pattern: /[?&]passcode=([^&\n?#]+)/ },
    { key: 'uname', pattern: /[?&]uname=([^&\n?#]+)/ },
    { key: 'role', pattern: /[?&]role=([^&\n?#]+)/ },
    { key: 'source', pattern: /[?&]source=([^&\n?#]+)/ }
  ];
  
  paramPatterns.forEach(({ key, pattern }) => {
    const match = url.match(pattern);
    if (match) {
      params[key] = decodeURIComponent(match[1]);
    }
  });
  
  return params;
};

/**
 * Generate embed URL for Zoom content
 */
export const generateZoomEmbedUrl = (url: string): string => {
  const lowerUrl = url.toLowerCase();
  
  // For recordings, try different embed approaches
  if (lowerUrl.includes('zoom.us/rec/')) {
    const recordingId = extractZoomRecordingId(url);
    if (recordingId) {
      // Try different embed URL formats for recordings
      return `https://zoom.us/rec/share/${recordingId}?autoplay=1&embed=1`;
    }
  }
  
  // For live meetings, create a join URL
  if (lowerUrl.includes('zoom.us/j/')) {
    const meetingId = extractZoomMeetingId(url);
    const password = extractZoomPassword(url);
    
    if (meetingId) {
      let embedUrl = `https://zoom.us/j/${meetingId}`;
      if (password) {
        embedUrl += `?pwd=${encodeURIComponent(password)}`;
      }
      return embedUrl;
    }
  }
  
  // Fallback to original URL
  return url;
};

/**
 * Check if Zoom URL can be embedded
 */
export const canEmbedZoomUrl = (url: string): boolean => {
  const lowerUrl = url.toLowerCase();
  
  // Cloud recordings are usually embeddable
  if (lowerUrl.includes('zoom.us/rec/')) {
    return true;
  }
  
  // Live meetings might be embeddable depending on settings
  if (lowerUrl.includes('zoom.us/j/')) {
    return true;
  }
  
  return false;
};

/**
 * Convert Google Drive URL to direct video URL
 */
export const convertGoogleDriveToDirect = (url: string): string => {
  const fileId = extractGoogleDriveId(url);
  if (fileId) {
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
  return url;
};

/**
 * Get YouTube embed URL
 */
export const getYouTubeEmbedUrl = (url: string): string => {
  const videoId = extractYouTubeId(url);
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

/**
 * Get YouTube thumbnail URL
 */
export const getYouTubeThumbnail = (url: string): string => {
  const videoId = extractYouTubeId(url);
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  }
  return '';
};

/**
 * Analyze video URL and return source information
 */
export const analyzeVideoUrl = (url: string): VideoSource => {
  if (!url) {
    return { type: 'unknown', url: '' };
  }

  const lowerUrl = url.toLowerCase();
  
  // YouTube
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    const videoId = extractYouTubeId(url);
    return {
      type: 'youtube',
      url: url,
      embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : url,
      thumbnailUrl: videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : ''
    };
  }
  
  // Google Drive
  if (lowerUrl.includes('drive.google.com')) {
    const fileId = extractGoogleDriveId(url);
    return {
      type: 'google-drive',
      url: url,
      embedUrl: fileId ? `https://drive.google.com/file/d/${fileId}/preview` : url,
      thumbnailUrl: ''
    };
  }
  
  // Zoom
  if (lowerUrl.includes('zoom.us')) {
    // Check if it's a cloud recording first
    if (isZoomRecording(url)) {
      const recordingId = extractZoomRecordingId(url);
      return {
        type: 'zoom-recording',
        url: url,
        embedUrl: url,
        thumbnailUrl: '',
        isRecording: true,
        recordingId: recordingId || undefined
      };
    }
    
    // Regular Zoom meeting
    const meetingId = extractZoomMeetingId(url);
    const password = extractZoomPassword(url);
    return {
      type: 'zoom',
      url: url,
      embedUrl: url, // Zoom URLs can be used directly
      thumbnailUrl: '',
      isRecording: false
    };
  }
  
  // Vimeo
  if (lowerUrl.includes('vimeo.com')) {
    const vimeoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
    return {
      type: 'vimeo',
      url: url,
      embedUrl: vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : url,
      thumbnailUrl: ''
    };
  }
  
  // HLS (m3u8)
  if (lowerUrl.endsWith('.m3u8') || lowerUrl.includes('m3u8')) {
    return {
      type: 'hls',
      url: url,
      embedUrl: url,
      thumbnailUrl: ''
    };
  }

  // Direct video file
  if (lowerUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/)) {
    return {
      type: 'direct',
      url: url,
      embedUrl: url,
      thumbnailUrl: ''
    };
  }

  // Dropbox shared links -> direct
  if (lowerUrl.includes('dropbox.com')) {
    const direct = url.replace('?dl=0', '?raw=1').replace('?dl=1', '?raw=1');
    return {
      type: 'direct',
      url: direct,
      embedUrl: direct,
      thumbnailUrl: ''
    };
  }
  
  // Check if it might be a video URL without extension
  if (lowerUrl.includes('video') || lowerUrl.includes('media') || lowerUrl.includes('stream')) {
    return {
      type: 'direct',
      url: url,
      embedUrl: url,
      thumbnailUrl: ''
    };
  }
  
  return {
    type: 'unknown',
    url: url,
    embedUrl: url,
    thumbnailUrl: ''
  };
};

/**
 * Check if video URL is supported
 */
export const isVideoUrlSupported = (url: string): boolean => {
  const source = analyzeVideoUrl(url);
  return source.type !== 'unknown';
};

/**
 * Get appropriate video player component based on URL type
 */
export const getVideoPlayerType = (url: string): 'html5' | 'youtube' | 'google-drive' | 'vimeo' | 'zoom' => {
  const source = analyzeVideoUrl(url);
  
  switch (source.type) {
    case 'youtube':
      return 'youtube';
    case 'google-drive':
      return 'google-drive';
    case 'vimeo':
      return 'vimeo';
    case 'zoom':
    case 'zoom-recording':
      return 'zoom';
    case 'direct':
    default:
      return 'html5';
  }
};
